import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    apiService.setAuthToken(null);
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      const userInfo = await apiService.getCurrentUser();
      setUser(userInfo);
      // Salvar informações do usuário no localStorage para outros componentes
      localStorage.setItem('user', JSON.stringify(userInfo));
      console.log('✅ Usuário autenticado:', userInfo);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Token inválido/expirado de verdade → desloga
        console.error('❌ Token inválido:', error);
        logout();
      } else {
        // Erro de REDE (offline, 502, cold start): mantém a sessão usando
        // o usuário em cache — NÃO desconecta o admin por falha temporária.
        const cached = localStorage.getItem('user');
        if (cached) {
          try { setUser(JSON.parse(cached)); } catch { logout(); }
        }
        console.warn('⚠️ Falha de rede ao validar sessão — mantendo login em cache');
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    // Verificar token no localStorage ao inicializar
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      setToken(storedToken);
      apiService.setAuthToken(storedToken);
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, [checkAuthStatus]);

  const login = async (username, password) => {
    try {
      console.log('🔐 Tentando fazer login...');
      const response = await apiService.login(username, password);
      
      const { access_token } = response;
      
      if (!access_token) {
        throw new Error('Token não recebido do servidor');
      }
      
      console.log('✅ Token recebido, salvando...');
      
      // Salvar token no localStorage
      localStorage.setItem('authToken', access_token);
      setToken(access_token);
      
      // Configurar token no apiService
      apiService.setAuthToken(access_token);
      
      // Obter informações do usuário
      const userInfo = await apiService.getCurrentUser();
      setUser(userInfo);
      
      // Salvar informações do usuário no localStorage para outros componentes
      localStorage.setItem('user', JSON.stringify(userInfo));
      
      console.log('✅ Login realizado com sucesso:', userInfo);
      
      return { success: true };
    } catch (error) {
      console.error('❌ Erro no login:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Erro no login' 
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};